import {format} from 'date-fns'
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {isFulfilled, isRejected} from '@reduxjs/toolkit'
import {generatePath, matchPath, useParams} from 'react-router-dom'
import {push} from 'connected-react-router'
import {useToasts} from 'react-toast-notifications'

import FacebookPreview from '../../PostCreation/PostPreview/Facebook'
import LinkedinPreview from '../../PostCreation/PostPreview/Linkedin'
import TwitterPreview from '../../PostCreation/PostPreview/Twitter'
import InstagramPreview from '../../PostCreation/PostPreview/Instagram'
import ChangeView from '../../ToggleMobileDesktopView'
import SocialToggler from '../../ToggleSocials'
import CustomButton from '../../UI/Buttons/CustomButton'
import SocialPostStatus from '../../SocialPostStatus'
import CheckboxInput from '../../UI/Inputs/Checkbox'

import {PostRestrictionHandler} from '../../../utils/postCreationHandler'
import {TOAST_MESSAGE} from '../../UI/ToastNotification/ToastNotification'
import {AUTH_ROUTE} from '../../../pages/App/routes'

import {useDispatch, useSelector} from '../../../store'
import {setSelectedPostSocialIdAction, setViewAction} from '../../../store/managePost/slice'
import {selectSocialMediaDataBySocialName} from '../../../selectors/ProjectSelector'

import {POST_SOCIAL_STATUS, SOCIAL_NETWORK, TPost} from '../../../models/post'
import {
    APPROVED,
    definePostSocialStatus,
    isShowSocialPostForExternal
} from '../../../models/postStatuses'
import {
    TApproveCurrentRoundPayload,
    TUnApproveCurrentRoundPayload,
    TLatestSchedule,
    TPostSocialManagePost,
    TStartNextApprovalRoundPayload,
    VIEW_TYPE
} from '../../../store/managePost/newTypes'
import {
    TProjectSocialFacebook,
    TProjectSocialInstagram,
    TProjectSocialLinkedin,
    TProjectSocialTwitter
} from '../../../store/project/types'

import './ListItem.scss'
import {OriginalSocialIcons} from '../../UI/Icons/socials'
import {getPublishDate} from '../../../utils/transformSchedule'
import {APPROVAL_LIST_TYPE} from '../../../store/application/types'
import {Roles} from '../../../models/roles'
import {getPostsForInstaGridViewThunk} from "../../../store/gridView/thunks";


type ApprovalListItemProps = {
    post: TPost
    latestSchedule: TLatestSchedule
    postSocials: TPostSocialManagePost[]
    requireApproverRevision?: (data: any) => any
    approveCurrentRound: (data: TApproveCurrentRoundPayload) => any
    startNextApprovalRound: (data: TStartNextApprovalRoundPayload) => any
    setCurrectPostToApproval: (post: any) => any
    setSelectedPosts: (event: any) => any
    checked?: boolean
    isSinglePost?: boolean
    project?: any
    socials: (
        | TProjectSocialFacebook
        | TProjectSocialInstagram
        | TProjectSocialLinkedin
        | TProjectSocialTwitter
        )[]
}

const ApprovalListItem: FC<ApprovalListItemProps> = ({
                                                         latestSchedule,
                                                         post,
                                                         postSocials,
                                                         approveCurrentRound,
                                                         startNextApprovalRound,
                                                         requireApproverRevision,
                                                         setSelectedPosts,
                                                         setCurrectPostToApproval,
                                                         isSinglePost,
                                                         project,
                                                         checked,
                                                         socials: socialsForSocialDetails
                                                     }) => {
    const [view, setView] = useState<VIEW_TYPE>(VIEW_TYPE.TYPE_DESKTOP)
    const dispatch = useDispatch()
    const {addToast} = useToasts()
    const {location, user, socialDetails, loaders, approvalListType, gridPosts,} = useSelector((state) => ({
        user: state.auth.user,
        location: state.router.location,
        socialDetails: selectSocialMediaDataBySocialName(state),
        loaders: state.managePost.loaders,
        approvalListType: state.app.approvalListType,
        gridPosts: state.gridView[SOCIAL_NETWORK.INSTAGRAM_SOCIAL].latestPosts,
    }))
    const {type: postType, master_social_post_id} = post
    const params = useParams()
    const socials = useMemo(
        () =>
            postSocials
                .filter((postSocial: TPostSocialManagePost) =>
                    isShowSocialPostForExternal(postSocial.status, user?.agency_role)
                )
                .map((postSocial: TPostSocialManagePost) => postSocial.social_network),
        [postSocials]
    )
    const [activeSocial, setActiveSocial] = useState<SOCIAL_NETWORK>(socials[0])

    const restrictions = useMemo(() => {
        return new PostRestrictionHandler({social: activeSocial, postType}).getContentRestrictions()
    }, [activeSocial, postType])

    const approveAndMoveToNextRound = async (id: number) => {
        const approveCurrentRoundResponse = await approveCurrentRound({post_social_id: id})
        if (isFulfilled(approveCurrentRoundResponse)) {
            addToast(TOAST_MESSAGE.POST_APPROVED, {appearance: 'success'})
        } else if (isRejected(approveCurrentRoundResponse)) {
            addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, {appearance: 'error'})
        }
    }

    const generatePathToSocial = (social: string) => {
        return (
            generatePath(approvalListType === APPROVAL_LIST_TYPE.APPROVED ? AUTH_ROUTE.UNAPPROVE_ITEM : AUTH_ROUTE.APPROVAL_ITEM, {
                projectId: project?.id,
                social
            }) + location.search
        )
    }

    useEffect(() => {
        const social = (params as any).social
        if (social) setActiveSocial(social)
    }, [location])

    const setActiveSocialAndMove = (social: SOCIAL_NETWORK) => {
        const {id} = postSocials.find(({social_network}) => social_network === social)!
        dispatch(setSelectedPostSocialIdAction(id))
        dispatch(push(generatePathToSocial(social)))
    }

    const requestRevision = async (post: any) => {
        requireApproverRevision && requireApproverRevision({postId: post.id, activeSocial})
        setCurrectPostToApproval(post)
    }

    const socialsToRender = useMemo(() => {
        return postSocials.reduce((result: any, postSocial: any) => {
            result[postSocial.social_network] = postSocial
            return result
        }, {})
    }, [postSocials])

    const publishDate = useMemo(() => getPublishDate(latestSchedule), [latestSchedule])

    const renderPostPreview = useMemo(() => {
        let preview = null

        const previewProps = {
            socialPost: socialsToRender[activeSocial],
            isMobile: view !== VIEW_TYPE.TYPE_DESKTOP,
            isGrid: view === VIEW_TYPE.TYPE_GRID,
            restrictions,
            socialDetails,
            latestSchedule: latestSchedule,
            posts: gridPosts,
            post:post
        }

        switch (activeSocial) {
            case SOCIAL_NETWORK.FACEBOOK_SOCIAL:
                preview = <FacebookPreview
                    {...previewProps}
                    publishDate={publishDate[SOCIAL_NETWORK.FACEBOOK_SOCIAL]}
                />
                break
            case SOCIAL_NETWORK.TWITTER_SOCIAL:
                preview = <TwitterPreview
                    {...previewProps}
                    publishDate={publishDate[SOCIAL_NETWORK.TWITTER_SOCIAL]}
                />
                break
            case SOCIAL_NETWORK.LINKEDIN_SOCIAL:
                preview = <LinkedinPreview
                    {...previewProps}
                    publishDate={publishDate[SOCIAL_NETWORK.LINKEDIN_SOCIAL]}
                />
                break
            case SOCIAL_NETWORK.INSTAGRAM_SOCIAL:
                preview = <InstagramPreview
                    {...previewProps}
                    publishDate={publishDate[SOCIAL_NETWORK.INSTAGRAM_SOCIAL]}
                />
                break
        }

        return preview
    }, [activeSocial, view, gridPosts])

    // TODO label2 post types ts enum
    const displayType = useMemo(() => (postType ? postType.split('_').join(' ') : ''), [postType])

    const isApproved = useMemo(() => {
        return (
            APPROVED === definePostSocialStatus(socialsToRender[activeSocial]?.status, user?.agency_role) || socialsToRender[activeSocial]?.is_approved
        )
    }, [socialsToRender, activeSocial, user])

    const isApprovingPostSocial = useCallback(
        (postId: number) => {
            return loaders.post.approve && loaders.post.processingIds.includes(postId)
        },
        [loaders.post.approve, loaders.post.processingIds]
    )

    // Grid view
    const isEnableGridView = useMemo(() => {
        switch (activeSocial) {
            case SOCIAL_NETWORK.INSTAGRAM_SOCIAL: {
                return true
            }
            default: {
                return false
            }
        }
    }, [activeSocial])


    const getPostsForGridView = async (view: VIEW_TYPE) => {
        setView(view);
        if (view == VIEW_TYPE.TYPE_GRID) {
            await dispatch(getPostsForInstaGridViewThunk())
        }
    }


    return (
        <div className='approval-item'>
            {!isSinglePost &&
            socialsToRender[activeSocial]?.status !== POST_SOCIAL_STATUS.STATUS_REQUIRES_REVISION && approvalListType !== APPROVAL_LIST_TYPE.APPROVED && (
                <div className='approval-item__checkbox'>
                    <CheckboxInput
                        type='checkbox'
                        checked={checked}
                        onChange={() => {
                            setSelectedPosts(post)
                        }}
                    />
                </div>
            )}
            <SocialToggler
                {...{
                    socials,
                    activeSocial: activeSocial ? activeSocial : (params as any).social,
                    setActiveSocial: !project ? setActiveSocial : setActiveSocialAndMove
                }}
            >
                <div className='approval-item__wrapper'>
                    <SocialPostStatus
                        className='approval-item__post-status'
                        status={socialsToRender[activeSocial]?.is_unapproved ? 'unapproved' : (socialsToRender[activeSocial].is_approved ? APPROVED : (socialsToRender[activeSocial]?.status || null))}
                        user={user || null}
                    />
                    <div className='approval-item__preview'>
                        <div className='approval-item__preview__post'>{renderPostPreview}</div>
                    </div>
                    <div className='approval-item__post-data'>
                        <div className='approval-item__post-data__type'>
                            {displayType.charAt(0).toUpperCase() + displayType.slice(1)}
                        </div>
                        <div className='approval-item__post-data__id'>
                            ID {socialsToRender[activeSocial]?.id}
                        </div>
                        <div className='approval-item__post-data__date'>
                            <div className='approval-item__post-data__date__title'>Publish date</div>
                            <div className='approval-item__post-data__date__value'>
                                {!!latestSchedule.id &&
                                format(
                                    new Date(`${latestSchedule.date}T${latestSchedule.time}`),
                                    'dd/MM/yy h:mm a'
                                )}
                            </div>
                        </div>
                        <div className='approval-item__post-data__social-networks'>
                            <div className='approval-item__post-data__social-networks__title'>
                                Social networks
                            </div>
                            <div className='approval-item__post-data__social-networks__value'>
                                <div className='post-socials'>
                                    {postSocials.map(
                                        ({social_network: socialNetwork}) =>
                                            !!socialDetails[socialNetwork] && (
                                                <div
                                                    key={`post-to-social-${socialNetwork}`}
                                                    className='post-socials__item-wrapper'
                                                >
                                                    <div className='post-socials__item'>
                                                        <div className={`post-socials__item__social`}>
                                                            {OriginalSocialIcons[socialNetwork]}
                                                        </div>
                                                        <div className='post-socials__item__data'>
                                                            {socialDetails[socialNetwork].logo.original ? (
                                                                <img
                                                                    className='post-socials__item__data__avatar'
                                                                    alt='avatar'
                                                                    src={socialDetails[socialNetwork].logo.original}
                                                                />
                                                            ) : (
                                                                socialDetails[socialNetwork].logo.default
                                                            )}
                                                            <div className='post-socials__item__data__name'>
                                                                {socialDetails[socialNetwork]!.name}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className='approval-item__post-data__audience'>
                            <div className='approval-item__post-data__audience__title'>Target audience</div>
                            <div className='approval-item__post-data__audience__value'>
                                Visibility: Published post (shown in page and can be promoted). Audience limiting:
                                everyone. News feed targeting: people of all ages.
                            </div>
                        </div>
                        {socialsToRender[activeSocial]?.status !==
                        POST_SOCIAL_STATUS.STATUS_REQUIRES_REVISION &&
                        (!master_social_post_id ||
                            socialsToRender[activeSocial]?.id === master_social_post_id) && (
                            <div className='approval-item__post-data__actions'>
                                {!isSinglePost && requireApproverRevision && !isApproved && approvalListType === APPROVAL_LIST_TYPE.PENDING && !!post.can_approve_post_socials.includes(socialsToRender[activeSocial]?.id) && (
                                    <CustomButton onClick={() => requestRevision(post)} button='secondary'>
                                        Request revision
                                    </CustomButton>
                                )}
                                {!isApproved && approvalListType === APPROVAL_LIST_TYPE.PENDING && !!post.can_approve_post_socials.includes(socialsToRender[activeSocial]?.id) && (
                                    <CustomButton
                                        onClick={() => approveAndMoveToNextRound(socialsToRender[activeSocial]?.id)}
                                        inProgress={isApprovingPostSocial(socialsToRender[activeSocial]?.id)}
                                        button='primary'
                                    >
                                        Approve
                                    </CustomButton>
                                )}
                                {!isSinglePost && isApproved && approvalListType === APPROVAL_LIST_TYPE.APPROVED && !!post.can_unapprove_post_socials.includes(socialsToRender[activeSocial]?.id) && (
                                    <CustomButton
                                        button='secondary'
                                        onClick={() => requestRevision(post)}
                                        inProgress={loaders.post.approve}
                                    >
                                        Unapprove and request revision
                                    </CustomButton>
                                )}
                            </div>
                        )}
                        <div className='approval-item__post-data__change-view'>
                            <ChangeView {...{view, changeView: getPostsForGridView, isEnableGridView}} />
                        </div>
                    </div>
                </div>
            </SocialToggler>
        </div>
    )
}

export default ApprovalListItem
